import { AdunitDeviceRuleConfig, CreateAdmStack } from '@pubstack/common/src/stack'

// WARNING: in the stack form the `adUnitIds` fields hold ad unit *names*, not ids.

export type StackFormFloorRuleConfig = NonNullable<CreateAdmStack['configuration']['floorRuleConfig']>

export type AdUnitNamesByDevice = {
  desktop: string[]
  mobile: string[]
}

/**
 * Drops from every refresh rule the ad units that are no longer part of the stack for that device, and
 * a rule left with no ad unit. The form refuses to save an empty rule, and removing it leaves no
 * remaining ad unit unassigned.
 */
export const pruneRefreshRulesToAdUnits = (rules: AdunitDeviceRuleConfig[], adUnitNames: AdUnitNamesByDevice): AdunitDeviceRuleConfig[] =>
  rules
    .map((rule) => {
      const namesToKeep = rule.device === 'desktop' ? adUnitNames.desktop : adUnitNames.mobile
      return { ...rule, adUnitIds: rule.adUnitIds.filter((adUnitName) => namesToKeep.includes(adUnitName)) }
    })
    .filter((rule) => rule.adUnitIds.length > 0)

/**
 * Turns the ad unit names a rule holds back into ids, dropping any name that no longer matches an
 * ad unit. An unmatched name used to be saved as an empty id, which no ad unit can ever match.
 */
export const resolveAdUnitIds = (adUnitNames: string[], idsByName: ReadonlyMap<string, string>): string[] =>
  adUnitNames.map((adUnitName) => idsByName.get(adUnitName)).filter((adUnitId): adUnitId is string => !!adUnitId)

/**
 * Turns the ad unit names of every specific floor rule back into ids. A rule left with no ad unit is
 * removed rather than saved empty, as when pruning.
 */
export const resolveFloorRuleConfig = (config: StackFormFloorRuleConfig, idsByName: ReadonlyMap<string, string>): StackFormFloorRuleConfig => ({
  standardFloorRuleId: config.standardFloorRuleId,
  specificFloorRuleConfigs: config.specificFloorRuleConfigs?.map((rule) => ({ ...rule, adUnitIds: resolveAdUnitIds(rule.adUnitIds, idsByName) })).filter((rule) => rule.adUnitIds.length > 0),
})

/**
 * Drops from every specific floor rule the ad units that are no longer part of the stack for that
 * device. Unlike a refresh rule, a specific floor rule left with no ad unit is removed: the standard
 * rule already covers the ad units it used to target.
 */
export const pruneFloorRuleConfigToAdUnits = (config: StackFormFloorRuleConfig, adUnitNames: string[]): StackFormFloorRuleConfig => {
  if (!config.specificFloorRuleConfigs) {
    return config
  }
  return {
    ...config,
    specificFloorRuleConfigs: config.specificFloorRuleConfigs
      .map((rule) => ({ ...rule, adUnitIds: rule.adUnitIds.filter((adUnitName) => adUnitNames.includes(adUnitName)) }))
      .filter((rule) => rule.adUnitIds.length > 0),
  }
}
